import React, { useEffect, useRef, useState } from "react";
import "./Tabs.scss";

/**
 * Tabs Component
 * Renders a tabbed interface with dynamic titles and content switching
 *
 * @param {ReactNode[]} children - Tab content, each child should include a `title` prop
 * @param {string} size - Size modifier class ('sm', 'md', 'lg'), default is 'md'
 * @param {number} defaultIndex - Initially active tab index, default is 0
 */
const Tabs = ({ children, size = "md", defaultIndex = 0 }) => {
  /* === useState hooks === */
  const [activeIndex, setActiveIndex] = useState(defaultIndex); // Active tab index
  const [tabWidth, setTabWidth] = useState(null);               // Width of tab nav, used to fix content width
  /* === end useState === */

  /* === useRef hook === */
  const navRef = useRef(null); // Reference to the tab navigation container
  /* === end useRef === */

  /* === Extract tab titles from children === */
  const tabTitles = React.Children.map(children, (child) => child.props.title);

  /* === useEffect to set tab content width based on nav width === */
  useEffect(() => {
    if (navRef.current) {
      setTabWidth(navRef.current.offsetWidth);
    }
  }, [tabTitles]); // Recalculate width when tab count/titles change
  /* === end useEffect === */

  return (
    <div className={`tabs ${size}`}>
      {/* === Tab Navigation Buttons === */}
      <div className="tab-nav" ref={navRef}>
        {tabTitles.map((title, index) => (
          <button
            key={index}
            className={`tab-button ${index === activeIndex ? "active" : ""}`}
            onClick={() => setActiveIndex(index)} // Switch tab on click
          >
            {title}
          </button>
        ))}
      </div>

      {/* === Tab Content Panel === */}
      <div
        className="tab-content"
        style={{ width: tabWidth ? `${tabWidth}px` : "auto" }} // Match content width to nav
      >
        {/* Only render active tab's content */}
        {React.Children.map(children, (child, index) =>
          index === activeIndex ? child : null
        )}
      </div>
    </div>
  );
};

export default Tabs;
